import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faEdit, 
  faEnvelope, 
  faPhone, 
  faMapMarkerAlt, 
  faGlobe, 
  faBuilding, 
  faBriefcase, 
  faGraduationCap, 
  faCircleCheck, 
  faCircleXmark,
  faChartBar
} from '@fortawesome/free-solid-svg-icons';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';
import AdminLayout from './AdminLayout';
import adminService from '../../services/adminService';
import '../admin/AdminStyles.css';

function ViewProfessionalListing() {
  const { id } = useParams();
  const [professional, setProfessional] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    async function loadData() {
      setIsLoading(true);
      try {
        const adminToken = localStorage.getItem('adminToken');
        const data = await adminService.getProfessionalListing(id, adminToken);
        setProfessional(data);
      } catch (error) {
        console.error('Error fetching professional listing:', error);
        setError('Failed to fetch professional listing details.');
      } finally {
        setIsLoading(false);
      }
    }
    
    loadData();
  }, [id]);
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="admin-loading-container">
          <div className="admin-loading">Loading professional listing details...</div>
        </div>
      </AdminLayout>
    );
  }
  
  if (error || !professional) {
    return (
      <AdminLayout>
        <div className="admin-page-header">
          <h1 className="admin-page-title">Error</h1>
        </div>
        <div className="admin-card">
          <div className="admin-card-body">
            <div className="admin-error-message">
              {error || "Professional listing not found"}
            </div>
            <div style={{ marginTop: '1.5rem' }}>
              <Link to="/admin/professional-listings" className="admin-btn">
                <FontAwesomeIcon icon={faArrowLeft} /> Back to Professional Listings
              </Link>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Professional Listing Details</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/professional-listings">Professional Listings</Link>
          </li>
          <li className="admin-breadcrumb-item active">{professional.name}</li>
        </ul>
      </div>
      
      <div className="admin-page-actions" style={{ marginBottom: '1.5rem' }}>
        <Link 
          to="/admin/professional-listings" 
          className="admin-btn admin-btn-light"
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Listings
        </Link>
        <Link 
          to={`/admin/professional-listings/${id}/edit`} 
          className="admin-btn admin-btn-primary"
        >
          <FontAwesomeIcon icon={faEdit} /> Edit Listing
        </Link>
      </div>
      
      <div className="admin-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '1.5rem' }}>
        <div className="admin-column" style={{ flex: '1', minWidth: '300px' }}>
          {/* Main Information */}
          <div className="admin-card">
            <div className="admin-card-header">
              <h2 className="admin-card-title">Basic Information</h2>
              <div className="admin-status-badge" style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '2rem',
                backgroundColor: professional.is_active ? '#d4edda' : '#f8d7da',
                color: professional.is_active ? '#155724' : '#721c24',
              }}>
                {professional.is_active ? 'Active' : 'Inactive'}
              </div>
            </div>
            
            <div className="admin-card-body">
              <div className="admin-view-item" style={{ marginBottom: '1.25rem' }}>
                <h3 className="admin-view-label" style={{ fontSize: '1.75rem', color: '#333', marginBottom: '0.25rem' }}>
                  {professional.name}
                </h3>
                <p className="admin-view-value" style={{ fontSize: '1.25rem', color: '#666', margin: '0' }}>
                  {professional.title || professional.qualification}
                </p>
              </div>
              
              <div className="admin-view-item" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                <FontAwesomeIcon icon={faBriefcase} style={{ marginRight: '0.75rem', color: 'var(--admin-primary)' }} />
                <div>
                  <div className="admin-view-label" style={{ fontWeight: '600', fontSize: '0.9rem', color: '#555' }}>Expertise</div>
                  <div className="admin-view-value" style={{ fontSize: '1.1rem' }}>{professional.expertise}</div>
                </div>
              </div>
              
              <div className="admin-view-item" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                <FontAwesomeIcon icon={faGraduationCap} style={{ marginRight: '0.75rem', color: 'var(--admin-primary)' }} />
                <div>
                  <div className="admin-view-label" style={{ fontWeight: '600', fontSize: '0.9rem', color: '#555' }}>Qualification</div>
                  <div className="admin-view-value" style={{ fontSize: '1.1rem' }}>{professional.qualification}</div>
                </div>
              </div>
              
              <div className="admin-view-item" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '0.75rem', color: 'var(--admin-primary)' }} />
                <div>
                  <div className="admin-view-label" style={{ fontWeight: '600', fontSize: '0.9rem', color: '#555' }}>Experience</div>
                  <div className="admin-view-value" style={{ fontSize: '1.1rem' }}>{professional.experience} years</div>
                </div>
              </div>
              
              {professional.organization && (
                <div className="admin-view-item" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                  <FontAwesomeIcon icon={faBuilding} style={{ marginRight: '0.75rem', color: 'var(--admin-primary)' }} />
                  <div>
                    <div className="admin-view-label" style={{ fontWeight: '600', fontSize: '0.9rem', color: '#555' }}>Organization</div>
                    <div className="admin-view-value" style={{ fontSize: '1.1rem' }}>{professional.organization}</div>
                  </div>
                </div>
              )}
              
              <div className="admin-view-item" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '0.75rem', color: 'var(--admin-primary)' }} />
                <div>
                  <div className="admin-view-label" style={{ fontWeight: '600', fontSize: '0.9rem', color: '#555' }}>Location</div>
                  <div className="admin-view-value" style={{ fontSize: '1.1rem' }}>{professional.location}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="admin-column" style={{ flex: '1', minWidth: '300px' }}>
          {/* Contact Information */}
          <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
            <div className="admin-card-header">
              <h2 className="admin-card-title">Contact Information</h2>
            </div>
            
            <div className="admin-card-body">
              <div className="admin-view-item" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '0.75rem', color: 'var(--admin-primary)' }} />
                <div>
                  <div className="admin-view-label" style={{ fontWeight: '600', fontSize: '0.9rem', color: '#555' }}>Email</div>
                  <div className="admin-view-value" style={{ fontSize: '1.1rem' }}>
                    <a href={`mailto:${professional.email}`} style={{ color: 'var(--admin-primary)' }}>
                      {professional.email}
                    </a>
                  </div>
                </div>
              </div>
              
              {professional.phone && (
                <div className="admin-view-item" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                  <FontAwesomeIcon icon={faPhone} style={{ marginRight: '0.75rem', color: 'var(--admin-primary)' }} />
                  <div>
                    <div className="admin-view-label" style={{ fontWeight: '600', fontSize: '0.9rem', color: '#555' }}>Phone</div>
                    <div className="admin-view-value" style={{ fontSize: '1.1rem' }}>
                      <a href={`tel:${professional.phone}`} style={{ color: 'var(--admin-primary)' }}>
                        {professional.phone}
                      </a>
                    </div>
                  </div>
                </div>
              )}
              
              {professional.website && (
                <div className="admin-view-item" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                  <FontAwesomeIcon icon={faGlobe} style={{ marginRight: '0.75rem', color: 'var(--admin-primary)' }} />
                  <div>
                    <div className="admin-view-label" style={{ fontWeight: '600', fontSize: '0.9rem', color: '#555' }}>Website</div>
                    <div className="admin-view-value" style={{ fontSize: '1.1rem' }}>
                      <a href={professional.website} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--admin-primary)' }}>
                        {professional.website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  </div>
                </div>
              )}
              
              {professional.linkedin && (
                <div className="admin-view-item" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                  <FontAwesomeIcon icon={faLinkedin} style={{ marginRight: '0.75rem', color: 'var(--admin-primary)' }} />
                  <div>
                    <div className="admin-view-label" style={{ fontWeight: '600', fontSize: '0.9rem', color: '#555' }}>LinkedIn</div>
                    <div className="admin-view-value" style={{ fontSize: '1.1rem' }}>
                      <a href={professional.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--admin-primary)' }}>
                        View Profile
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Payment and Status Information */}
          <div className="admin-card">
            <div className="admin-card-header">
              <h2 className="admin-card-title">Status Information</h2>
            </div>
            
            <div className="admin-card-body">
              <div className="admin-view-item" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                <FontAwesomeIcon 
                  icon={professional.is_paid ? faCircleCheck : faCircleXmark} 
                  style={{ 
                    marginRight: '0.75rem',
                    color: professional.is_paid ? 'var(--admin-success)' : 'var(--admin-danger)'
                  }} 
                />
                <div>
                  <div className="admin-view-label" style={{ fontWeight: '600', fontSize: '0.9rem', color: '#555' }}>Payment Status</div>
                  <div className="admin-view-value" style={{ fontSize: '1.1rem' }}>
                    <span style={{ 
                      color: professional.is_paid ? 'var(--admin-success)' : 'var(--admin-danger)'
                    }}>
                      {professional.is_paid ? 'Paid' : 'Unpaid'}
                    </span>
                  </div>
                </div>
              </div>
              
              {professional.payment_date && (
                <div className="admin-view-item" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '25px', marginRight: '0.75rem' }}></div>
                  <div>
                    <div className="admin-view-label" style={{ fontWeight: '600', fontSize: '0.9rem', color: '#555' }}>Payment Date</div>
                    <div className="admin-view-value" style={{ fontSize: '1.1rem' }}>
                      {formatDate(professional.payment_date)}
                    </div>
                  </div>
                </div>
              )}
              
              {professional.payment_amount && (
                <div className="admin-view-item" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '25px', marginRight: '0.75rem' }}></div>
                  <div>
                    <div className="admin-view-label" style={{ fontWeight: '600', fontSize: '0.9rem', color: '#555' }}>Payment Amount</div>
                    <div className="admin-view-value" style={{ fontSize: '1.1rem' }}>
                      {formatCurrency(professional.payment_amount)}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="admin-view-item" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                <FontAwesomeIcon 
                  icon={professional.is_active ? faCircleCheck : faCircleXmark} 
                  style={{ 
                    marginRight: '0.75rem',
                    color: professional.is_active ? 'var(--admin-success)' : 'var(--admin-danger)'
                  }} 
                />
                <div>
                  <div className="admin-view-label" style={{ fontWeight: '600', fontSize: '0.9rem', color: '#555' }}>Status</div>
                  <div className="admin-view-value" style={{ fontSize: '1.1rem' }}>
                    <span style={{ 
                      color: professional.is_active ? 'var(--admin-success)' : 'var(--admin-danger)'
                    }}>
                      {professional.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="admin-view-item" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '25px', marginRight: '0.75rem' }}></div>
                <div>
                  <div className="admin-view-label" style={{ fontWeight: '600', fontSize: '0.9rem', color: '#555' }}>Valid Until</div>
                  <div className="admin-view-value" style={{ fontSize: '1.1rem' }}>
                    {formatDate(professional.valid_until)}
                  </div>
                </div>
              </div>
              
              <div className="admin-view-item" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '25px', marginRight: '0.75rem' }}></div>
                <div>
                  <div className="admin-view-label" style={{ fontWeight: '600', fontSize: '0.9rem', color: '#555' }}>Created On</div>
                  <div className="admin-view-value" style={{ fontSize: '1.1rem' }}>
                    {formatDate(professional.created_at)}
                  </div>
                </div>
              </div>
              
              {professional.updated_at && (
                <div className="admin-view-item" style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '25px', marginRight: '0.75rem' }}></div>
                  <div>
                    <div className="admin-view-label" style={{ fontWeight: '600', fontSize: '0.9rem', color: '#555' }}>Last Updated</div>
                    <div className="admin-view-value" style={{ fontSize: '1.1rem' }}>
                      {formatDate(professional.updated_at)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {professional.bio && (
        <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
          <div className="admin-card-header">
            <h2 className="admin-card-title">About</h2>
          </div>
          
          <div className="admin-card-body">
            <div className="admin-view-value" style={{ lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
              {professional.bio}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ViewProfessionalListing;
